import { api } from "@/lib/api-client"

export type SlideAddSource = "library" | "preview" | "dragdrop" | "library-deck"
export type PresentMode = "presentation" | "presenter_view"

// Must match the backend cap (_CLIENT_SLIDE_IDS_BATCH_MAX). Decks larger
// than this are split across multiple POSTs so a 201-slide click still
// records every event instead of silently 400-ing.
const BATCH_CHUNK_SIZE = 200

export function useSlideSenseTelemetry() {
  return {
    slideAddedToBuilder(slideId: string, source: SlideAddSource) {
      api
        .post("/slide-sense/events/client-event", {
          event: "slide_added_to_builder",
          slide_id: slideId,
          context: { source },
        })
        .catch(() => {
          // telemetry is fire-and-forget — never surface errors
        })
    },
    /**
     * Batched variant — one HTTP POST per BATCH_CHUNK_SIZE ids. The
     * backend fans each request out into N individual
     * slide_added_to_builder events, so downstream analytics see the
     * same shape as N single calls.
     */
    slidesAddedToBuilderBatch(slideIds: string[], source: SlideAddSource) {
      if (slideIds.length === 0) return
      for (let i = 0; i < slideIds.length; i += BATCH_CHUNK_SIZE) {
        const chunk = slideIds.slice(i, i + BATCH_CHUNK_SIZE)
        api
          .post("/slide-sense/events/client-event", {
            event: "slide_added_to_builder",
            slide_ids: chunk,
            context: { source },
          })
          .catch(() => {
            // telemetry is fire-and-forget — never surface errors
          })
      }
    },
    /**
     * One event per presentation start — unlike slideAddedToBuilder this is
     * NOT fanned out per slide, since presenting is a single session, not a
     * per-slide action. So a deck over BATCH_CHUNK_SIZE can't be split
     * across multiple POSTs the way slidesAddedToBuilderBatch does — that
     * would record multiple presentation sessions for one presentation.
     * Instead, truncate slide_ids to the cap: the session (and its
     * distinct-slides count, undercounted only for the excess) still gets
     * recorded, instead of the whole event 400-ing and being dropped.
     */
    deckPresented(slideIds: string[], mode: PresentMode, collectionId: string | null) {
      if (slideIds.length === 0) return
      api
        .post("/slide-sense/events/client-event", {
          event: "deck_presented",
          slide_ids: slideIds.slice(0, BATCH_CHUNK_SIZE),
          mode,
          collection_id: collectionId,
        })
        .catch(() => {
          // telemetry is fire-and-forget — never surface errors
        })
    },
  }
}
